import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {Services} from "../../Services";
import {ErrorCode} from "../../../network/status/error/ErrorCode";
import {Resources} from "../../../resources/Resources";
import {ICommunityCalendarEventModel} from "../../../models/community/calendar/ICommunityCalendarEventModel";
import {ICommunityCalendarEventCreateOutgoingModel} from "../../../models/community/calendar/ICommunityCalendarEventCreateOutgoingModel";
import {ICommunityCalendarEventUpdateModel} from "../../../models/community/calendar/ICommunityCalendarEventUpdateModel";
import {ICommunityArticleProposalsItemModel} from "../../../models/community/article/ai/proposals/ICommunityArticleProposalsItemModel";
import {CommunityCalendarEventType} from "../../../models/community/calendar/CommunityCalendarEventType";
import {ICommunityCalendarOutgoingModel} from "../../../models/community/calendar/ICommunityCalendarOutgoingModel";
import {ICommunityCalendarShareModel} from "../../../models/community/calendar/ICommunityCalendarShareModel";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";

export class CommunityCalendarService {

    public static updateEvents: ((event: ICommunityCalendarEventModel) => void)[] = [];
    public static calendarUpdateEvents: ((calendar: ICommunityCalendarOutgoingModel) => void)[] = [];

    public static dispose(): void {
        this.updateEvents = [];
        this.calendarUpdateEvents = [];
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_CALENDAR_UPDATE, (data: ICommunityCalendarOutgoingModel) => {
            this.calendarUpdateEvents.forEach(callback => callback(data));
        });
    }

    public static onUpdateEvent(component: INetworkComponent, func: (event: ICommunityCalendarEventModel) => void) {
        this.updateEvents.push(func);
        component.onRemove(() => arrayRemove(this.updateEvents, func));
    }

    public static onCalendarUpdate(component: INetworkComponent, callback: (calendar: ICommunityCalendarOutgoingModel) => void): void {
        this.calendarUpdateEvents.push(callback);
        component.onRemove(() => arrayRemove(this.calendarUpdateEvents, callback));
    }

    public static async getEvents(communityId: string, minTime: number, maxTime: number, component?: INetworkComponent): Promise<ICommunityCalendarOutgoingModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/calendar/${communityId}/${minTime}/${maxTime}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async createEvent(communityId: string, event: ICommunityCalendarEventCreateOutgoingModel, component?: INetworkComponent): Promise<ICommunityCalendarEventModel> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.SCHEDULED_TIME_IN_PAST, message: Resources.t("words.errorSCHEDULED_TIME_IN_PAST")},
            {errorCode: ErrorCode.SCHEDULED_TIME_TOO_FAR, message: Resources.t("words.errorSCHEDULED_TIME_TOO_FAR")},
            {errorCode: ErrorCode.SCHEDULED_KEYWORD_ALREADY_EXISTS, message: Resources.t("words.errorSCHEDULED_KEYWORD_ALREADY_EXISTS")}
        ]);
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/calendar/${communityId}`, event, component);
        if (request.status == HttpStatus.OK) {
            this.updateEvents.forEach(func => func(request.data));
            return request.data;
        }
        return undefined;
    }

    public static async updateEvent(communityId: string, eventId: string, update: ICommunityCalendarEventUpdateModel, component?: INetworkComponent): Promise<ICommunityCalendarEventModel> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.SCHEDULED_TIME_IN_PAST, message: Resources.t("words.errorSCHEDULED_TIME_IN_PAST")},
            {errorCode: ErrorCode.SCHEDULED_TIME_TOO_FAR, message: Resources.t("words.errorSCHEDULED_TIME_TOO_FAR")},
            {errorCode: ErrorCode.SCHEDULED_KEYWORD_ALREADY_EXISTS, message: Resources.t("words.errorSCHEDULED_KEYWORD_ALREADY_EXISTS")}
        ]);
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/calendar/${communityId}/${eventId}`, update, component);
        if (request.status == HttpStatus.OK) {
            this.updateEvents.forEach(func => func(request.data));
            return request.data;
        }
        return undefined;
    }

    public static async refresh(communityId: string, type: CommunityCalendarEventType, minTime: number, maxTime: number, component?: INetworkComponent): Promise<ICommunityCalendarOutgoingModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/calendar/${communityId}/refresh/${type}/${minTime}/${maxTime}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async deleteEvent(communityId: string, eventId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.delete(ProductType.COMMUNITY, `/community/calendar/${communityId}/${eventId}`, component);
        return request.status == HttpStatus.OK;
    }

    public static async getSharedEvents(shareToken: string, minTime: number, maxTime: number, component?: INetworkComponent): Promise<ICommunityCalendarShareModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/share/${shareToken}/calendar/${minTime}/${maxTime}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getEventRules(communityId: string, eventId: string, component?: INetworkComponent): Promise<ICommunityArticleProposalsItemModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/calendar/${communityId}/${eventId}/rules`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }
}
